import React, { useContext, useEffect, useState } from "react";
import { AutoCompleteComponent, FilteringEventArgs } from "@syncfusion/ej2-react-dropdowns";
import useGetAllCnaeEmployee from "../../../../services/utils/useGetAllCnaeEmployee.";
import useGetAllCnaeCompany from "../../../../services/utils/useGetAllCnaeCompnany "
import { MasterDataContext } from "components/pages/Delta/Delta";
import { getRawFechaCNAE, getFechaCNAE } from '../../../../services/utils/fechaCNAEUtils';
import masterDataUtils from "services/utils/masterDataUtils";
import { useFormContext } from "react-hook-form";
import Fuse from "fuse.js";
import DOMPurify from 'dompurify';

interface IInputProps {
  errors: any;
  register: any;
  watchCnaeCode: any;
  setValue?: any;
  idCode: string;
  idDescription: string;
}

const MainEconomicActivityCodeCNAE: React.FC<IInputProps> = ({
  errors,
  register,
  setValue,
  watchCnaeCode,
  idCode,
  idDescription
}) => {
  const [allCnae, setAllCnae] = useState<any>();
  const [localCnaeError, setLocalCnaeError] = useState<string>('');
  const [fechaCNAE, setFechaCNAE] = useState<string>('');
  const { getAllCnaeCompany, watchFechaCNAE } = useGetAllCnaeCompany();
  const itemKey: string = "cnae";
  const masterDataContext = useContext(MasterDataContext);
  const masterData = masterDataContext?.masterData;
  const { checkAndUpdate } = masterDataUtils(masterDataContext);

  const formContext = useFormContext();
  const { getValues, setError, clearErrors } = formContext;

  useEffect(() => {
    setFechaCNAE(getFechaCNAE());
    const stop = watchFechaCNAE((fecha) => {
      setFechaCNAE(fecha);
    });
    return () => stop && stop();
  }, []);

  useEffect(() => {
    if (getRawFechaCNAE() === null)
      return;

    const load = async (fecha: string) => {
      const res = await getAllCnaeCompany(fecha || '');
      if (res) {
        const arr = Array.isArray(res) ? [...res] : (res as any[]);
        setAllCnae(arr as any[]);
      }
    };
    load(getFechaCNAE());
  }, [fechaCNAE]);

  useEffect(() => {
    const validateCurrentCode = () => {
      try {
        const currentCodeRaw = getValues ? getValues(idCode) : '';
        const currentCode = (currentCodeRaw || '').toString().trim();
        if (!currentCode) {
          return;
        }

        if (currentCode.length < 3) {
          return;
        }

        if (allCnae) {
          const exists = (allCnae as any[]).some((item: any) => {
            const codigo = (item?.Codigo ?? item?.code ?? '').toString().trim();
            return codigo === currentCode;
          });
          if (!exists) {
            try { setValue && setValue(idCode, ""); } catch (e) { }
            const errorMessage = `${currentCode} CNAE no existe`;
            try { setError && setError(idCode, { type: 'manual', message: errorMessage }); } catch (e) { }
            setLocalCnaeError(errorMessage);
          } else {
            try { clearErrors && clearErrors(idCode); } catch (e) { }
            setLocalCnaeError('');
          }
        }
      } catch (e) { }
    };
    validateCurrentCode();
  }, [allCnae]);

  useEffect(() => {
    setValueCnaeDescription();
  }, [watchCnaeCode, allCnae]);

  useEffect(() => {
    setValueCnaeDescription();
  }, [allCnae]);
  const handleChange = (args: any) => {
    if (allCnae && allCnae) {
      const codeValue = allCnae?.find(
        (item: any) => item?.Descripcion === args.value
      );
      if (codeValue) {
        setValue(idCode, codeValue ? codeValue?.Codigo : "");
        try { clearErrors && clearErrors(idCode); } catch (e) { }
        setLocalCnaeError('');
      }
    }
  };

  const setValueCnaeDescription = () => {
    if (allCnae && allCnae) {
      const descriptionValue = allCnae?.find(
        (item: any) => item.Codigo === watchCnaeCode
      );
      if (descriptionValue) {
        setValue(
          idDescription,
          descriptionValue ? descriptionValue.Descripcion : ""
        );
        try { clearErrors && clearErrors(idCode); } catch (e) { }
        setLocalCnaeError('');
      } else {
        try { setValue && setValue(idDescription, ""); } catch (e) { }
        const currentCode = (watchCnaeCode || '').toString().trim();

        // si no hay código, o aún no tiene longitud completa, no mostramos error de "no existe"
        if (!currentCode || currentCode.length < 3) {
          return;
        }

        try { setValue && setValue(idCode, ""); } catch (e) { }
        const errorMessage = `${currentCode} CNAE no existe`;
        try { setError && setError(idCode, { type: 'manual', message: errorMessage }); } catch (e) { }
        setLocalCnaeError(errorMessage);
      }
    }
  };

  const itemsData: { [key: string]: Object }[] = allCnae;
  const fields: object = { value: "Descripcion" };

  function onFiltering(e: FilteringEventArgs) {
    let options: Object = {
      keys: ["Descripcion"],
      includeMatches: true,
      findAllMatches: true,
    };

    let fuse: any = new Fuse(itemsData, options);
    let result: any = fuse.search(e.text);
    let data: { [key: string]: Object }[] = [];
    for (let i: number = 0; i < result.length; i++) {
      data.push(result[i].item as any);
    }
    e.updateData(data, undefined);

    let lists: any = document
      ?.getElementById("agreement_popup")
      ?.querySelectorAll(".e-list-item");
    if (lists) {
      highlightSearch(lists, result as any);
    }
  }

  function highlightSearch(listItems: Element[], result: any): void {
    if (result.length > 0) {
      for (let i: number = 0; i < listItems.length; i++) {
        let innerHTML: string = listItems[i].innerHTML;
        for (
          let j: number = result[i].matches[0].indices.length - 1;
          j >= 0;
          j--
        ) {
          let indexes: number[] = result[i].matches[0].indices[j];
          innerHTML =
            innerHTML.substring(0, indexes[0]) +
            '<span class="e-highlight">' +
            innerHTML.substring(indexes[0], indexes[1] + 1) +
            "</span>" +
            innerHTML.substring(indexes[1] + 1);
          listItems[i].innerHTML = DOMPurify.sanitize(innerHTML);
        }
      }
    }
  }

  const handleInputBlur = () => {
    const rawValue = getValues(idCode);
    const value = (rawValue || '').toString().trim();
    if (value === "") {
      setValue(idDescription, "");
      return;
    }

    // mientras no tenga la longitud completa, tratamos como vacío sin error de "no existe"
    if (value.length < 3) {
      setValue(idDescription, "");
      return;
    }
    // if not empty, validate that a description exists
    const found = allCnae?.find((item: any) => (item.Codigo || '').toString().trim() === value);
    if (!found) {
      try { setValue && setValue(idDescription, ""); } catch (e) { }
      try { setValue && setValue(idCode, ""); } catch (e) { }
      const errorMessage = `${value} CNAE no existe`;
      try { setError && setError(idCode, { type: 'manual', message: errorMessage }); } catch (e) { }
      setLocalCnaeError(errorMessage);
    } else {
      try { clearErrors && clearErrors(idCode); } catch (e) { }
      setLocalCnaeError('');
    }
  };

  const handleAutoCompleteBlur = () => {
    const value = getValues(idDescription);
    if (value === "") {
      setValue(idCode, "");
    }
  };

  const handleAutoCompleteChange = (args: any) => {
    if (!args.isInteracted) return;
    handleChange(args);
    if (!args.value) {
      setValue(idCode, "");
      try { clearErrors && clearErrors(idCode); } catch (e) { }
      setLocalCnaeError('');
    }
  };

  const cnaeCodeId = idCode ? idCode : "cnaeCode";
  const cnaeDescd = idDescription ? idDescription : "cnaeDescription";

  return (
    <fieldset
      className={`input-block input-block--code ${(errors && errors[idCode ? idCode : "cnaeCode"]?.message) ||
        (errors &&
          errors[idDescription ? idDescription : "cnaeDescription"]?.message)
        ? "error"
        : ""
        }`}
    >
      <div className="container-leyend">
        {" "}
        <legend className="input-block--code__legend">
          CNAE para cotización AT/EP
        </legend>
      </div>
      <div className="row">
        <div className="col-sm-12 col-md-3 col-lg-3">
          <input
            className=" "
            type="text"
            name={cnaeCodeId}
            id={cnaeCodeId}
            {...register(cnaeCodeId, {
              required: "CNAE requerido",
              pattern: {
                value: /^\d{3}$/,
                message: "CNAE codigo debe terenr 3 digitos",
              },
            })}
            maxLength={3}
            minLength={3}
            onBlur={handleInputBlur}
          />
        </div>

        {itemsData && itemsData ? (
          <div className="col-sm-12 col-md-9 delta-autocomplete">
            <AutoCompleteComponent
              change={handleAutoCompleteChange}
              id={cnaeDescd}
              name={cnaeDescd}
              dataSource={itemsData}
              filtering={onFiltering}
              fields={fields}
              placeholder="CNAE para cotización AT/EP"
              autofill={true}
              {...register(cnaeDescd)}
              popupHeight="250px"
              popupWidth="250px"
              onBlur={handleAutoCompleteBlur}
            />
          </div>
        ) : null}
      </div>
      {errors && (
        <>
          <p className="error m-0">
            {errors[idCode ? idCode : "cnaeCode"]?.message}
          </p>
          <p className="error m-0">
            {errors[idDescription ? idDescription : "cnaeDescription"]?.message}
          </p>
        </>
      )}
      {localCnaeError ? <p className="error m-0">{localCnaeError}</p> : null}
    </fieldset>
  );
};

export default MainEconomicActivityCodeCNAE;
